import CustomSelect from "@/components/shared/CustomSelect";
import ApplicantDocumentType from "@/model/interface/ApplicantDocumentType";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import FileUploader from "./FileUploader";
import { Plus, X, Save } from "lucide-react";

interface NewApplicantIDProps {
  documentTypes: ApplicantDocumentType[];
  onDocumentSubmit: (documentType: string, documentFields: Record<string, any>, documentFiles: File[]) => {};
}

const inputClass =
  "w-full h-11 rounded-lg border border-gray-300 bg-white px-4 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0]";

const NewApplicantID: React.FC<NewApplicantIDProps> = ({ documentTypes, onDocumentSubmit }) => {
  const [newDocumentUI, setNewDocumentUI] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<ApplicantDocumentType>(documentTypes[0]);

  const [files, setFiles] = useState<File[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    mode: "onSubmit",
    defaultValues: {},
    shouldUnregister: true,
  });

  const onSubmit = async (formData: Record<string, object>) => {
    onDocumentSubmit(selectedDocumentType.type, formData, files);
  };

  const onFileUploaded = (allFiles: File[]) => {
    setFiles(allFiles);
  };

  const documentTypeOnChange = (value: any) => {
    setSelectedDocumentType(value);
  };

  const toggleNewDocumentUI = () => {
    setNewDocumentUI(!newDocumentUI);
  };

  return (
    <div>
      {!newDocumentUI && (
        <button
          type="button"
          onClick={toggleNewDocumentUI}
          className="inline-flex items-center gap-2 rounded-full bg-[#9BFBE0] px-6 py-2.5 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all cursor-pointer"
        >
          <Plus className="size-4" />
          Add new document
        </button>
      )}
      {newDocumentUI && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document type</label>
              <CustomSelect
                value={selectedDocumentType}
                onChange={documentTypeOnChange}
                options={documentTypes}
                mapOptionToLabel={(o: ApplicantDocumentType) => `${o.name} (${o.pointsValue} pts)`}
                mapOptionToValue={(o: ApplicantDocumentType) => o.type}
              />
            </div>

            {selectedDocumentType.description && (
              <p className="text-xs text-gray-500">{selectedDocumentType.description}</p>
            )}

            {selectedDocumentType.fields?.map((field, index) => {
              const validation = field.validation;
              return (
                <div key={`${field.name}-${index}`}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  {field.description && <p className="text-xs text-gray-500 mb-1">{field.description}</p>}
                  <input
                    type={field.type}
                    className={inputClass}
                    {...register(field.name, {
                      required: true,
                      ...(validation ? { pattern: new RegExp(validation) } : {}),
                    })}
                  />
                  {errors[field.name] && <p className="text-xs text-red-500 mt-1">This field is required.</p>}
                </div>
              );
            })}

            <FileUploader
              title="Upload documents"
              subtitle={selectedDocumentType.name}
              entityType="Document"
              onFileUploaded={onFileUploaded}
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#9BFBE0] px-6 py-2.5 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all cursor-pointer"
            >
              <Save className="size-4" />
              Save Document
            </button>
            <button
              type="button"
              onClick={toggleNewDocumentUI}
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all cursor-pointer"
            >
              <X className="size-4" />
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default NewApplicantID;
